import React from 'react';
import { withRouter } from 'react-router-dom';
import { find, get, head, isArray } from 'lodash';
import searchQuery from 'query-string';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';

// local dependencies
import { ToDoItemDetailSummaryWidgetService } from 'services/toDoItemDetailSummaryWidget';
import { toDoItemDetailSummaryWidget as ToDoItemDetailSummaryWidgetView } from 'ui/views/widgets/toDoItemDetailSummaryWidget';
import { SERVICE_RESPONSE_STATUS, WIDGET_ERROR_TEMPLATE, SIZES } from 'constants/common';
import { overlay } from 'modules/overlay';
import { OVERLAY_VIEWS } from 'constants/templates';
import * as errorOverlay from 'utils/errorOverlay';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import { DASHBOARD_SUB_ROUTES } from 'constants/route-paths';
import { REDUCER } from 'constants/entity-names';
import { initCallState } from 'store/actions/callState';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { ToDoItemDetailSummaryWidget } from 'ui/containers/ToDoItemDetailSummaryWidget';
 *
 * // render
 * <ToDoItemDetailSummaryWidget propName='propValue'/>
 */
export class _ToDoItemDetailSummaryWidget extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        this.handleActionClick = this.handleActionClick.bind( this );
        this.viewCaseData = this.viewCaseData.bind( this );
        this.initiateOutboundCall = this.initiateOutboundCall.bind( this );

        // component state
        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: undefined,
        };

        // create service instance
        this.toDoItemDetailSummaryWidgetService = new ToDoItemDetailSummaryWidgetService();

        // start data fetch
        this.fetchComponentData();
    }

    async fetchComponentData() {
        log.debug( 'ToDoItemDetailSummaryWidget.fetchComponentData()' );

        // cancel ongoing request
        this.toDoItemDetailSummaryWidgetService.cancel();

        // get status and service data
        const { status, data } = await this.toDoItemDetailSummaryWidgetService.fetchComponentData( {
            url: this.props.widget.url,
            data: {
                ...this.props.requestPayload
            }
        } );

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            } );
        } else {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                componentData: data,
            } );
        }
    }

    /**
     * @desc View Case Read Only Form.
     */
    async viewCaseData( { url } ) {
        log.debug( 'ToDoItemDetailsSummaryWidget.viewCaseData()' );

        // cancel ongoing request
        this.toDoItemDetailSummaryWidgetService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // get status and service data
        const { status, data } = await this.toDoItemDetailSummaryWidgetService.viewCaseData( {
            url: url,
            data: this.props.requestPayload
        } );

        // show page level preloader
        pageLevelPreloader.hide();

        if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            log.debug( data );
            overlay.open( {
                settings: {
                    modalType: OVERLAY_VIEWS.VIEW_CASE,
                    showCloseButton: false,
                    ignoreOutsideClick: false,
                },
                config: {},
                data: {
                    ...data,
                    onCancelClick: () => {
                        overlay.close();
                    },
                }
            } );
        } else {
            errorOverlay.open(); // show error
        }

    }

    /**
     * @desc Handle Initiate Call button click
     */
    async initiateOutboundCall( { url } ) {
        log.debug( 'ToDoItemDetailsSummaryWidget.initiateOutboundCall()' );

        // cancel ongoing request
        this.toDoItemDetailSummaryWidgetService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        const memberData = get( find( this.state.componentData.primarySection, { name: 'memberId'}, {} ), 'value', [] );

        // get status and service data
        const { status, data } = await this.toDoItemDetailSummaryWidgetService.initiateOutboundCall( {
            url: url,
            data: {
                data: {
                    memberId: isArray( memberData ) ? head(  memberData ):  memberData
                }
            }
        } );

        // show page level preloader
        pageLevelPreloader.hide();

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            // save call data in the store
            this.props.initCallState( data );
            
            // change to authorization flow route, 2nd step
            this.props.history.push( {
                pathname: DASHBOARD_SUB_ROUTES.CALL_AUTHORIZATION_FLOW,
                search: searchQuery.stringify( {
                    step: 2,
                } ),
            } );
            
        } else {

            if( WIDGET_ERROR_TEMPLATE.TO_DO_OUTBOUND_CALL_FAILED === get( data, 'template', undefined ) ) {
                errorOverlay.open( { message: data.description, size: SIZES.SMALL } );
            } else {
                errorOverlay.open(); // show error

            }
        }
    }

    /**
     * @desc Handle action click.
     */
    handleActionClick( { value } ) {
        log.debug( 'ToDoItemDetailSummaryWidget.handleActionClick()' );

        if ( 'viewCase' === value.name ) {
            this.viewCaseData( {
                url: value.url
            } );
        } else if ( 'initiateCall' === value.name ) {
            this.initiateOutboundCall( {
                url: value.url
            } );
        }
    }

    /**
     * @desc When component unmounts, cleanup resources.
     */
    componentWillUnmount() {
        this.toDoItemDetailSummaryWidgetService.cancel(); // cancel ongoing requests
    }

    /**
     * @desc Render a React view component
     */
    render() {
        return (
            <ToDoItemDetailSummaryWidgetView
                isFetching={ this.state.isFetching }
                networkStatus={ this.state.networkStatus }
                componentData={ get( this.state, 'componentData', {} ) }
                onActionClick={this.handleActionClick}
            />
        );
    }
}

// set display name
_ToDoItemDetailSummaryWidget.displayName = 'ToDoItemDetailSummaryWidget';

// set default props
_ToDoItemDetailSummaryWidget.defaultProps = {};

// prop types
_ToDoItemDetailSummaryWidget.propTypes = {};

// map state to props
const mapStateToProps = ( state ) => {
    return {
        callState: state[ REDUCER.CALL_STATE ],
    };
};

// map dispatch to props
const mapDispatchToProps = ( dispatch ) => {
    return {
        initCallState: bindActionCreators( initCallState, dispatch ),
    };
};

export const ToDoItemDetailSummaryWidget = withRouter( connect( mapStateToProps, mapDispatchToProps )( _ToDoItemDetailSummaryWidget ) );
